'use client';

import HeaderSkeleton, { type OwnProps as HeaderProps } from '@carvana/header';
import { useClientSide } from '@carvana/merch-react-hooks';
import { MfeLoad } from '@carvana/microfrontend';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { getMfeCacheBuster } from '@/common/MicroFrontEnds';
import { commonEnvironment, staticCdnUrl } from '~/constants/environment';
import { useShowComposerTreatment } from '~/context/composer/ComposerContext';
import { wait } from '~/utilities';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';

type OwnProps = {
  cvIdToken?: string;
};

function useShowChatUi(): boolean {
  const pathname = usePathname();

  return !pathname?.startsWith('/new');
}

/**
 * Client Component for PageHeader
 * This is a simplfied page header that have access to context providers
 * The server component CarvanaLayoutHeader handles the context logic
 */
function ClientPageHeader({ cvIdToken }: OwnProps) {
  const isClientSide = useClientSide();
  const showComposerTreatment = useShowComposerTreatment();
  const showChatUi = useShowChatUi() && !showComposerTreatment;

  useEffect(() => {
    wait(100).then(() => {
      void MfeLoad({
        files: [
          [
            'HeaderModule',
            [
              {
                component: 'default',
                default: {
                  file: 'index.js',
                  manifest: `${staticCdnUrl}/header-module/header/assets-manifest.json?v=${getMfeCacheBuster()}`
                }
              }
            ]
          ]
        ]
      });
    });
  }, []);

  const headerProps = {
    cookies: cvIdToken ? { CVIdToken: cvIdToken } : {},
    environment: commonEnvironment,
    showChatUi,
    showComposerTreatment
  } as HeaderProps;

  return (
    <div id="mfe-header-wrapper">
      {isClientSide ? (
        <MfeReactComponent
          component="default"
          fallback={{
            Component: <HeaderSkeleton {...headerProps} />,
            timeout: 30000
          }}
          loading={<HeaderSkeleton {...headerProps} />}
          module="HeaderModule"
          {...headerProps}
        />
      ) : (
        <HeaderSkeleton {...headerProps} />
      )}
    </div>
  );
}

export default withErrorBoundary<OwnProps>(ClientPageHeader);
